<template>
    <div class="mx-auto max-w-4xl">
        <div class="flex justify-center">
            <div class="flex items-start gap-4">
                <div class="flex flex-col items-center">
                    <div class="relative">
                        <!-- Create a placeholder div with fixed dimensions when no image -->
                        <div v-if="!podcaster?.picture_url" class="inline-block size-20 rounded-full bg-gray-200 md:size-32"></div>
                        <a v-else :key="podcaster.id" :href="route('clipfinder.podcasters.show', podcaster.slug)">
                            <picture class="inline-block size-20 rounded-full bg-gray-400 ring-2 ring-gray-100 md:size-32">
                                <source
                                    class="size-20 rounded-full md:size-32"
                                    :srcset="podcaster.webp_picture_url ?? undefined"
                                    type="image/webp"
                                    height="160"
                                    width="160"
                                />
                                <img
                                    class="size-20 rounded-full md:size-32"
                                    :src="podcaster.picture_url"
                                    :alt="podcaster.name"
                                    height="160"
                                    width="160"
                                    loading="lazy"
                                />
                            </picture>
                        </a>

                        <!-- Button container with absolute positioning -->
                        <div class="absolute -bottom-5 left-1/2 -translate-x-1/2 transform">
                            <FollowButton
                                v-if="podcaster && loggedIn"
                                :podcaster-id="podcaster.id"
                                :initial-is-following="isFollowing(podcaster.id)"
                                :is-loading="isLoadingFollowState"
                                @follow-toggled="handleFollowToggle"
                                class="px-2 py-1 text-xs"
                            />
                        </div>
                    </div>
                </div>

                <PageTitle v-if="!isSeemoreView" class="mt-4">
                    Start listening to {{ totalPodcasts }}
                    <br />
                    <PageTitleHighlight v-if="podcaster">{{ podcaster.name }}</PageTitleHighlight>
                    <br />
                    <span v-if="selectedCategory">{{ selectedCategory.name }}</span> podcasts
                </PageTitle>
            </div>
        </div>

        <p v-if="podcaster?.description" class="mx-auto mt-6 max-w-2xl text-center text-sm leading-6 text-slate-600 md:text-base">
            {{ podcaster.description }}
        </p>

        <nav v-if="podcaster" :aria-label="`${podcaster.name} sections`" class="mt-6 flex flex-wrap justify-center gap-2">
            <Link
                v-for="item in navigation"
                :key="item.label"
                :href="item.href"
                class="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-primary hover:text-primary"
                :class="{ 'border-primary bg-primary text-white hover:text-white': item.active }"
            >
                {{ item.label }}
            </Link>
        </nav>

        <div v-if="podcaster && categories?.length" class="mt-4 flex flex-wrap justify-center gap-2" aria-label="Podcast topics">
            <Link
                v-for="category in categories"
                :key="category.id"
                :href="route('clipfinder.podcaster.category.show', [podcaster.slug, category.slug])"
                class="text-xs font-medium text-slate-500 underline decoration-slate-300 underline-offset-4 hover:text-primary"
            >
                {{ category.name }}
            </Link>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, onMounted, watch } from 'vue'
import PageTitleHighlight from '@/Pages/Podcaster/PageTitleHighlight.vue'
import PageTitle from '@/Pages/Podcaster/PageTitle.vue'
import FollowButton from '@/Components/FollowButton.vue'
import type { PodcasterResource, CategoryResource } from '@/types'
import { storeToRefs } from 'pinia'
import { usePodcasterStore } from '@/Stores/podcaster'
import { definePodcastStore } from '@/Stores/podcast'
import { useUserStore } from '@/Stores/user'
import { ScrollState } from '@/Enum/ScrollState'
import { Link } from '@inertiajs/vue3'

const props = defineProps<{
    podcaster?: PodcasterResource
    totalPodcasts: number
    selectedCategory?: CategoryResource
    categories?: CategoryResource[]
    isSeemoreView?: boolean
    invisiblePodcastersCount: number
    isLoadingFollowState?: boolean
}>()

const route = inject<any>('route')
const navigation = computed(() => {
    if (!props.podcaster) return []

    const currentRoute = route().current()

    return [
        {
            label: 'Overview',
            href: route('clipfinder.podcasters.show', props.podcaster.slug),
            active: currentRoute === 'clipfinder.podcasters.show' || currentRoute === 'clipfinder.podcaster.category.show',
        },
        {
            label: 'Episodes',
            href: route('clipfinder.podcasters.podcasts.index', props.podcaster.slug),
            active: currentRoute === 'clipfinder.podcasters.podcasts.index' || currentRoute === 'clipfinder.podcaster.podcasts.video',
        },
        {
            label: 'Clips',
            href: route('clipfinder.podcaster.clips.index', props.podcaster.slug),
            active: currentRoute === 'clipfinder.podcaster.clips.index',
        },
    ]
})

const podcasterStore = usePodcasterStore()
podcasterStore.selectPodcasters(props.podcaster?.id ? [props.podcaster.id] : [])

const store = definePodcastStore(props.podcaster)
const { totalPodcasts } = storeToRefs(store)
const { loggedIn } = storeToRefs(useUserStore())
const { followedPodcasters } = storeToRefs(podcasterStore)
const isLoadingFollowState = ref(false)

const isFollowing = (podcasterId: string) => {
    return followedPodcasters.value.some((p) => p.id === podcasterId) || false
}

const handleFollowToggle = async (podcasterId: string) => {
    isLoadingFollowState.value = true
    try {
        await podcasterStore.toggleFollowPodcaster(podcasterId)

        // Force refresh of followed podcasters list
        await podcasterStore.fetchFollowedPodcasters()
    } catch (e: any) {
        ScrollState.Failed
    } finally {
        isLoadingFollowState.value = false
    }
}

const fetchInitialFollowState = async () => {
    if (!loggedIn.value || !props.podcaster?.id) return

    isLoadingFollowState.value = true
    try {
        await podcasterStore.fetchFollowedPodcasters()

        // Ensure we have a valid array even if empty
        if (!followedPodcasters.value) {
            followedPodcasters.value = []
        }
    } catch (error) {
        ScrollState.Failed
    } finally {
        isLoadingFollowState.value = false
    }
}

onMounted(() => {
    fetchInitialFollowState()
})

// Watch for podcaster changes or login state changes
watch([() => props.podcaster?.id, () => loggedIn.value], async ([newId, newLoggedIn]) => {
    if (newId && newLoggedIn) {
        await fetchInitialFollowState()
    }
})
</script>
